import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { finleyAssert } from '@finley-technologies/common';

import { Alert } from 'packages/common-frontend/components/Alert';
import { Typography } from 'packages/common-frontend/components/Typography';
import { TextInput } from '~/core/routes/settings/implementations/common/Inputs';

type FormData = {
  newName: string;
};

export const ConfirmDuplicateModal = ({
  isOpen,
  existingEntityName,
  existingNames,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  existingEntityName: string;
  existingNames: string[];
  onClose: () => void;
  onConfirm: (newName: string) => void;
}) => {
  const form = useForm<FormData>({
    defaultValues: { newName: '' },
  });

  useEffect(() => {
    if (isOpen) form.reset({ newName: '' });
  }, [isOpen, form]);

  const { newName } = useWatch({ control: form.control });
  const newNameAlreadyExists = Boolean(existingNames?.includes(newName ?? ''));

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <FormProvider {...form}>
          <form
            onSubmit={form.handleSubmit((data) => {
              onConfirm(data.newName);
            })}
          >
            <ModalHeader fontSize="lg" fontWeight="bold">
              Duplicate {existingEntityName}
            </ModalHeader>

            <ModalBody>
              <TextInput<FormData>
                name="newName"
                label="Name"
                placeholder="Enter"
                required
              />
              {newNameAlreadyExists && (
                <Alert status="error">
                  <Typography variant="base">
                    That name&apos;s already in use. Choose a unique name.
                  </Typography>
                </Alert>
              )}
            </ModalBody>

            <ModalFooter>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                type="submit"
                colorScheme="v2-aurora"
                ml={3}
                isDisabled={newNameAlreadyExists}
              >
                Add
              </Button>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  );
};

type SetConfirmDuplicateFn = (args: SetConfirmDuplicatePayload) => void;

type SetConfirmDuplicatePayload = {
  existingEntityName: string;
  callback: (newName: string) => void;
  existingNames?: string[];
};

const ConfirmDuplicateContext = React.createContext<{
  showConfirmDuplicateModal: SetConfirmDuplicateFn;
}>({ showConfirmDuplicateModal: () => {} });

export const ConfirmDuplicateModalProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [
    confirmDuplicateFn,
    setConfirmDuplicateFn,
  ] = useState<SetConfirmDuplicatePayload | null>(null);

  return (
    <ConfirmDuplicateContext.Provider
      value={{ showConfirmDuplicateModal: setConfirmDuplicateFn }}
    >
      <ConfirmDuplicateModal
        isOpen={Boolean(confirmDuplicateFn)}
        existingEntityName={confirmDuplicateFn?.existingEntityName ?? ''}
        onClose={() => setConfirmDuplicateFn(null)}
        onConfirm={(newName) => {
          confirmDuplicateFn?.callback(newName);
          setConfirmDuplicateFn(null);
        }}
        existingNames={confirmDuplicateFn?.existingNames ?? []}
      />
      {children}
    </ConfirmDuplicateContext.Provider>
  );
};

export const useConfirmDuplicateContext = () => {
  const context = React.useContext(ConfirmDuplicateContext);

  finleyAssert(
    context,
    'ConfirmDuplicateContext must be used within a ConfirmDuplicateModalProvider',
  );

  return context;
};
